/*
  Inline validation for required Infinite Options fields (e.g. Proof).

  Infinite Options blocks add-to-cart when a required field is empty, but only
  reports it through window.alert(). In-app browsers can suppress that alert,
  it never names the field, and it never scrolls to it, so on mobile the button
  appears to do nothing. This listener runs in the capture phase, before the
  app's click handler, and shows the message inline next to the field instead.
  When every required field is filled it steps aside and the app, UploadKit and
  product-form.js handle the click exactly as before.
*/
(() => {
  const SUBMIT_SELECTOR = 'product-form .product-form__submit';
  const CONTAINER_SELECTOR = '#infiniteoptions-container';
  const ERROR_CLASS = 'io-field-error';
  const INVALID_CLASS = 'io-field--invalid';
  const MESSAGES = {
    'properties[Proof]': 'Please choose a proof option before adding to cart.',
  };

  const wrapperOf = (field) => field.closest(`${CONTAINER_SELECTOR} > div`) || field.parentElement;

  const groupInputs = (field) =>
    field.type === 'radio' ? [...document.querySelectorAll(`input[type="radio"][name="${CSS.escape(field.name)}"]`)] : [field];

  const isVisible = (field) => wrapperOf(field).getClientRects().length > 0;

  const isEmpty = (field) => {
    if (field.type === 'radio') return !groupInputs(field).some((input) => input.checked);
    if (field.type === 'checkbox') return !field.checked;
    return !String(field.value || '').trim();
  };

  const requiredFields = (container) => {
    const seenGroups = new Set();
    return [...container.querySelectorAll('select[required], input[required], textarea[required]')].filter((field) => {
      if (field.type === 'file' || field.disabled || !isVisible(field)) return false;
      if (field.type === 'radio') {
        if (seenGroups.has(field.name)) return false;
        seenGroups.add(field.name);
      }
      return true;
    });
  };

  const labelText = (field) => {
    const wrapper = wrapperOf(field);
    const label = (field.id && document.querySelector(`label[for="${CSS.escape(field.id)}"]`)) || wrapper.querySelector('label');
    return label ? label.textContent.trim() : '';
  };

  const messageFor = (field) => {
    if (MESSAGES[field.name]) return MESSAGES[field.name];
    const label = labelText(field);
    return label ? `Please choose an option for "${label}" before adding to cart.` : 'Please complete this option before adding to cart.';
  };

  const showError = (field) => {
    const wrapper = wrapperOf(field);
    let message = wrapper.querySelector(`.${ERROR_CLASS}`);
    if (!message) {
      message = document.createElement('p');
      message.className = ERROR_CLASS;
      message.id = `${(field.id || field.name).replace(/[^\w-]+/g, '-')}-error`;
      message.setAttribute('role', 'alert');
      wrapper.appendChild(message);
    }
    message.textContent = messageFor(field);
    wrapper.classList.add(INVALID_CLASS);
    groupInputs(field).forEach((input) => {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', message.id);
    });
  };

  const clearError = (wrapper) => {
    wrapper.classList.remove(INVALID_CLASS);
    wrapper.querySelector(`.${ERROR_CLASS}`)?.remove();
    wrapper.querySelectorAll('[aria-invalid]').forEach((input) => {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(SUBMIT_SELECTOR);
      if (!button) return;

      const container = document.querySelector(CONTAINER_SELECTOR);
      if (!container) return;

      // Only validate the form these options belong to (not a quick-add elsewhere on the page).
      const formId = button.form?.getAttribute('id');
      const optionsFormId = container.getAttribute('form');
      if (formId && optionsFormId && formId !== optionsFormId) return;

      const invalid = requiredFields(container).filter(isEmpty);
      if (!invalid.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      invalid.forEach(showError);

      const first = invalid[0];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      wrapperOf(first).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      // Focus after the scroll so iOS doesn't open the picker over the message.
      setTimeout(() => first.focus({ preventScroll: true }), 400);
    },
    true
  );

  document.addEventListener('change', (event) => {
    const field = event.target;
    if (!field.closest?.(CONTAINER_SELECTOR)) return;
    const wrapper = wrapperOf(field);
    if (!wrapper.classList.contains(INVALID_CLASS)) return;
    const stillEmpty = [...wrapper.querySelectorAll('select[required], input[required], textarea[required]')].some(isEmpty);
    if (!stillEmpty) clearError(wrapper);
  });

  /*
    The "STEP 4 – Add Proof?" heading is a separate custom-liquid block placed after
    the Infinite Options app block and pulled up with a negative margin-top so it sits
    above the Proof field. That anchors it to the bottom of the options, so anything
    that makes the options taller (the error message above, a conditional field)
    slides it down onto the Proof label. Move it inside the Proof field instead so it
    travels with the field. Without JS the original layout is left untouched.
  */
  let proofHeading = null;

  const attachProofHeading = () => {
    const proof = document.querySelector(`${CONTAINER_SELECTOR} .proof`);
    if (!proof) return;

    if (!proofHeading) {
      const candidate = proof.closest('.shopify-app-block')?.nextElementSibling;
      if (!candidate || !/margin-top:\s*-\d+px/.test(candidate.getAttribute('style') || '')) return;
      proofHeading = candidate;
      proofHeading.style.marginTop = '0';
      proofHeading.style.marginBottom = '16px';
    }

    // Re-attach if Infinite Options rebuilds the Proof field.
    if (proofHeading.parentElement !== proof) {
      proof.classList.add('proof--with-heading');
      proof.prepend(proofHeading);
    }
  };

  const watchOptions = () => {
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return false;
    attachProofHeading();
    new MutationObserver(attachProofHeading).observe(container, { childList: true, subtree: true });
    return true;
  };

  if (!watchOptions()) {
    const pageObserver = new MutationObserver(() => {
      if (watchOptions()) pageObserver.disconnect();
    });
    pageObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
